/**
 * Marketplace reporting.
 *
 * The money rules are the backend's and stated once there: revenue is CAPTURED payments
 * only, refunds are subtracted and dated to the ORIGINAL payment so a closed month does
 * not drift, and seed payments are excluded unless you ask for them. This page's job is to
 * keep those rules visible, because a revenue figure whose definition is invisible is a
 * figure somebody will quote wrongly.
 *
 * What it adds over a list of totals is the ratios. ₹68,865 net says nothing on its own;
 * "12% of what we took went back" and "two thirds of bookings were cancelled" are the
 * numbers a person acts on, and both were already in the payload — nobody had divided
 * them.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CalendarRange,
  IndianRupee,
  Loader2,
  Receipt,
  RotateCcw,
  Store,
  TrendingUp,
  UserPlus,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { DetailPanel, PanelEmpty, PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { BarList, TimeSeries, inr as chart_inr } from '@/components/charts';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { BoolFilter, FilterBar, type ChipInput } from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { bool_param, string_param, use_url_state } from '@/hooks/use-url-state';
import { humanize, inr } from '@/lib/format';
import { use_report_summary } from '@/features/reports/hooks';

/** One number as a share of another, or null when the denominator is zero. */
function share(part: number, whole: number): number | null {
  return whole > 0 ? (part / whole) * 100 : null;
}

const pct = (n: number | null): string => (n === null ? '—' : `${n.toFixed(n < 10 ? 1 : 0)}%`);

export function ReportPage() {
  // Dates live in the URL so a range can be shared or bookmarked.
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [dummy, set_dummy] = use_url_state('include_dummy', bool_param);
  const include_dummy = dummy === 'true';

  const { data, isLoading, isFetching, error, refetch } = use_report_summary({
    from: from || undefined,
    to: to || undefined,
    include_dummy,
  });

  const derived = useMemo(() => {
    if (!data) return null;
    const gross = Number(data.totals.gross_inr);
    const refunded = Number(data.totals.refunded_inr);
    const bookings = data.bookings_by_status.reduce((n, r) => n + r.count, 0);
    const cancelled =
      data.bookings_by_status.find((r) => r.status === 'CANCELLED')?.count ?? 0;
    const completed =
      data.bookings_by_status.find((r) => r.status === 'COMPLETED')?.count ?? 0;
    return {
      gross,
      refunded,
      bookings,
      cancelled,
      completed,
      /** How much of what we took went back out. */
      refund_rate: share(refunded, gross),
      /** The number that says whether the marketplace is working. */
      cancellation_rate: share(cancelled, bookings),
      completion_rate: share(completed, bookings),
      average_payment: data.totals.payments > 0 ? gross / data.totals.payments : 0,
      new_users: data.growth_by_day.reduce((n, d) => n + d.users, 0),
      new_vendors: data.growth_by_day.reduce((n, d) => n + d.vendors, 0),
    };
  }, [data]);

  const chips: ChipInput[] = [
    (from || to) && {
      key: 'date',
      label: 'Period',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
    dummy === 'true' && {
      key: 'dummy',
      label: 'Seed data',
      value: 'Counted as revenue',
      on_clear: () => set_dummy(''),
    },
  ];

  return (
    <DashboardLayout
      title="Reports"
      subtitle={
        from || to
          ? `Revenue, bookings and growth · ${describe_range(from, to)}`
          : 'Revenue, bookings and growth over the last 30 days'
      }
      actions={isFetching ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : undefined}
      stats={<MetricStrip metrics={build_metrics(data, derived)} />}
    >
      <FilterBar
        chips={chips}
        on_clear_all={() => {
          set_from('');
          set_to('');
          set_dummy('');
        }}
      >
        <DateRangeFilter
          from={from}
          to={to}
          on_change={(next) => {
            set_from(next.from);
            set_to(next.to);
          }}
          label="Period"
        />
        {/*
          Seed payments are excluded by default so a revenue figure is quotable. On
          staging, where nearly everything is seeded, this toggle is the only way to see a
          non-zero chart at all — which is exactly why it has to be visible rather than
          buried, and why the chip says "counted as revenue" when it is on.
        */}
        <BoolFilter
          label="Seed payments"
          value={dummy}
          on_change={(v) => set_dummy(v)}
          true_label="Counted"
          false_label="Excluded"
          any_label="Excluded"
          test_id="report-include-dummy"
        />
      </FilterBar>

      {error ? (
        <ErrorState
          title="Could not load the reports"
          description="Nothing has been changed. Try again."
          action={
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading || !data || !derived ? (
        <div className="grid gap-gutter">
          <DetailPanel title="Net revenue per day" icon={TrendingUp}>
            <PanelSkeleton rows={4} />
          </DetailPanel>
        </div>
      ) : (
        <div className="grid gap-gutter">
          {include_dummy && (
            <p
              className="flex items-start gap-2 rounded-control bg-warning/10 px-3 py-2 text-data font-medium text-warning"
              data-testid="dummy-warning"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Seed and test payments are being counted as revenue. Nothing on this page is
                quotable while that is on.
              </span>
            </p>
          )}

          <DetailPanel
            title="Net revenue per day"
            icon={TrendingUp}
            description={`Captured payments minus refunds, dated by capture${include_dummy ? '' : ' · seed payments excluded'}`}
            test_id="revenue-chart"
          >
            <TimeSeries
              label="Net revenue per day"
              format={(n) => chart_inr(n)}
              points={data.revenue_by_day.map((d) => ({
                label: d.date.slice(5),
                value: Number(d.net_inr),
              }))}
            />
          </DetailPanel>

          <div className="grid items-start gap-gutter lg:grid-cols-2">
            <DetailPanel
              title="Bookings by vertical"
              icon={BarChart3}
              description={`${derived.bookings.toLocaleString('en-IN')} booked in this period`}
              test_id="bookings-by-module"
            >
              <BarList
                label="Bookings by vertical"
                points={data.bookings_by_module.map((r) => ({
                  label: humanize(r.module_type),
                  value: r.count,
                }))}
              />
            </DetailPanel>

            <DetailPanel
              title="What became of them"
              icon={Activity}
              description={
                derived.cancellation_rate === null
                  ? undefined
                  : `${pct(derived.cancellation_rate)} cancelled, ${pct(derived.completion_rate)} completed`
              }
              test_id="bookings-by-status"
            >
              <BarList
                label="Bookings by status"
                points={data.bookings_by_status.map((r) => ({
                  label: humanize(r.status),
                  value: r.count,
                }))}
              />
              {/*
                The ratio, not just the bars. Two thirds cancelled and two thirds completed
                look almost identical as a bar chart and mean opposite things.
              */}
              {derived.cancellation_rate !== null && derived.cancellation_rate > 50 && (
                <p
                  className="mt-3 text-micro font-medium text-destructive"
                  data-testid="cancellation-warning"
                >
                  More than half of everything booked in this period was cancelled.
                </p>
              )}
            </DetailPanel>
          </div>

          <div className="grid items-start gap-gutter lg:grid-cols-2">
            <DetailPanel
              title="Vendor KYC funnel"
              icon={Store}
              description="A snapshot of every vendor, not just this period"
              test_id="kyc-funnel"
            >
              <BarList
                label="Vendors by KYC status"
                points={data.kyc_funnel.map((r) => ({
                  label: humanize(r.kyc_status),
                  value: r.count,
                }))}
              />
            </DetailPanel>

            <DetailPanel
              title="New signups per day"
              icon={UserPlus}
              description={`${derived.new_users} customers and ${derived.new_vendors} vendor profiles in this period`}
              test_id="growth-chart"
            >
              <TimeSeries
                label="New users per day"
                points={data.growth_by_day.map((d) => ({
                  label: d.date.slice(5),
                  value: d.users,
                }))}
              />
            </DetailPanel>
          </div>

          <DetailPanel
            title="Top vendors by net revenue"
            icon={Store}
            description="Booking payments only — plan, package and shop revenue is not attributed to one vendor"
            body_class="p-0"
            test_id="top-vendors"
          >
            {data.top_vendors.length === 0 ? (
              <div className="p-3.5">
                <PanelEmpty icon={Receipt}>
                  No vendor took any money in this period.
                </PanelEmpty>
              </div>
            ) : (
              <ul className="divide-y divide-border" data-testid="top-vendor-rows">
                {data.top_vendors.map((v, i) => (
                  <li
                    key={v.vendor_profile_id}
                    className="grid items-center gap-x-4 gap-y-1 px-3.5 py-2.5 sm:grid-cols-[2rem_1fr_8rem_9rem]"
                  >
                    <span className="text-micro font-bold text-muted-foreground tabular">
                      {i + 1}
                    </span>
                    <Link
                      to={`/vendors/${v.vendor_profile_id}`}
                      className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                    >
                      {v.business_name ?? v.vendor_profile_id}
                    </Link>
                    <span className="text-micro text-muted-foreground tabular">
                      {v.bookings} booking{v.bookings === 1 ? '' : 's'}
                    </span>
                    <span className="text-right font-semibold tabular">{inr(v.net_inr)}</span>
                  </li>
                ))}
              </ul>
            )}
          </DetailPanel>
        </div>
      )}
    </DashboardLayout>
  );
}

function build_metrics(
  data: ReturnType<typeof use_report_summary>['data'],
  d: {
    refund_rate: number | null;
    cancellation_rate: number | null;
    average_payment: number;
    bookings: number;
  } | null,
): Metric[] {
  return [
    {
      key: 'net',
      label: 'Net revenue',
      value: data ? inr(data.totals.net_inr) : undefined,
      hint: data ? `${inr(data.totals.gross_inr)} taken` : undefined,
      icon: IndianRupee,
      loading: !data,
    },
    {
      key: 'refund_rate',
      label: 'Refund rate',
      /*
       * The ratio, not the amount. "₹12,098 refunded" needs the gross beside it before it
       * means anything, and nobody was doing that division.
       */
      value: d ? pct(d.refund_rate) : undefined,
      hint: data ? `${inr(data.totals.refunded_inr)} back` : undefined,
      tone: d && d.refund_rate !== null && d.refund_rate > 20 ? 'destructive' : 'default',
      icon: RotateCcw,
      loading: !data,
    },
    {
      key: 'payments',
      label: 'Payments',
      value: data ? String(data.totals.payments) : undefined,
      hint: d && d.average_payment > 0 ? `${inr(d.average_payment)} average` : undefined,
      icon: Receipt,
      loading: !data,
    },
    {
      key: 'bookings',
      label: 'Bookings',
      value: d ? d.bookings.toLocaleString('en-IN') : undefined,
      hint: 'Made in this period',
      icon: CalendarRange,
      loading: !data,
    },
    {
      key: 'cancel_rate',
      label: 'Cancelled',
      // The single number that says whether the marketplace is actually working.
      value: d ? pct(d.cancellation_rate) : undefined,
      hint: 'of everything booked',
      tone:
        d && d.cancellation_rate !== null && d.cancellation_rate > 50 ? 'destructive' : 'default',
      icon: Activity,
      loading: !data,
    },
  ];
}
